import { useCallback, useMemo } from 'react';

/**
 * Snapshot de estadísticas en un momento determinado
 */
interface HistoricalSnapshot {
  timestamp: string;
  totalProjects: number;
  activeProjects: number;
  totalTenants: number;
  activeTenants: number;
  totalUsers: number;
  activeUsersCount: number;
}

/**
 * Estructura de datos históricos almacenados
 */
interface HistoricalData {
  snapshots: HistoricalSnapshot[];
}

/**
 * Indicador de cambio temporal
 */
export interface ChangeIndicator {
  value: number; // Porcentaje absoluto (ej: 15.3)
  direction: 'up' | 'down' | 'neutral';
  period: '7d' | '30d';
}

/**
 * Estadísticas parciales para crear snapshot
 */
interface SnapshotData {
  totalProjects: number;
  activeProjects: number;
  totalTenants: number;
  activeTenants: number;
  totalUsers: number;
  activeUsersCount: number;
}

const STORAGE_KEY = 'wayline-superadmin-stats-history';
const MAX_SNAPSHOTS_DAYS = 90;
const MIN_SNAPSHOT_INTERVAL_HOURS = 24;

/**
 * Hook para gestión de datos históricos de estadísticas del dashboard
 *
 * @version 1.0
 * @module core/dashboard/hooks
 *
 * @description
 * Gestiona snapshots de estadísticas en localStorage para mostrar
 * indicadores de cambio temporal en KPI Cards.
 *
 * @remarks
 * **Características:**
 * - Almacena snapshots en localStorage con timestamp
 * - Calcula cambios porcentuales vs períodos anteriores (7d, 30d)
 * - Limpia automáticamente snapshots antiguos (>90 días)
 * - Evita duplicados (máximo 1 snapshot cada 24 horas)
 * - Sin dependencias externas (solo localStorage)
 *
 * **Formato de almacenamiento:**
 * ```json
 * {
 *   "snapshots": [
 *     {
 *       "timestamp": "2026-02-12T10:00:00Z",
 *       "totalProjects": 48,
 *       "activeProjects": 42,
 *       "totalTenants": 16,
 *       "activeTenants": 14,
 *       "totalUsers": 95,
 *       "activeUsersCount": 82
 *     }
 *   ]
 * }
 * ```
 *
 * @example
 * ```tsx
 * const { saveSnapshot, getChange } = useHistoricalStats();
 *
 * // Guardar snapshot actual
 * saveSnapshot(stats);
 *
 * // Obtener cambio vs 7 días atrás
 * const change = getChange('totalProjects', '7d');
 * // Retorna: { value: 15.3, direction: 'up', period: '7d' }
 * ```
 */
export function useHistoricalStats() {
  /**
   * Obtiene los datos históricos del localStorage
   */
  const getStoredData = useCallback((): HistoricalData => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return { snapshots: [] };
      }
      return JSON.parse(stored) as HistoricalData;
    } catch (error) {
      console.error('Error al leer datos históricos:', error);
      return { snapshots: [] };
    }
  }, []);

  /**
   * Guarda los datos históricos en localStorage
   */
  const setStoredData = useCallback((data: HistoricalData): void => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error al guardar datos históricos:', error);
    }
  }, []);

  /**
   * Limpia snapshots antiguos (>90 días)
   *
   * @remarks
   * Se ejecuta automáticamente al guardar un nuevo snapshot
   */
  const cleanOldSnapshots = useCallback((): void => {
    const data = getStoredData();
    const now = new Date();
    const maxAgeMs = MAX_SNAPSHOTS_DAYS * 24 * 60 * 60 * 1000;

    const filteredSnapshots = data.snapshots.filter((snapshot) => {
      const snapshotDate = new Date(snapshot.timestamp);
      const ageMs = now.getTime() - snapshotDate.getTime();
      return ageMs <= maxAgeMs;
    });

    if (filteredSnapshots.length !== data.snapshots.length) {
      setStoredData({ snapshots: filteredSnapshots });
    }
  }, [getStoredData, setStoredData]);

  /**
   * Guarda un nuevo snapshot de estadísticas
   *
   * @param stats - Estadísticas actuales a guardar
   *
   * @remarks
   * - Solo guarda si no existe un snapshot del mismo día
   * - Limpia automáticamente snapshots antiguos después de guardar
   * - Ordena snapshots por timestamp (más recientes primero)
   */
  const saveSnapshot = useCallback(
    (stats: SnapshotData): void => {
      const data = getStoredData();
      const now = new Date();

      // Verificar si ya existe un snapshot reciente (últimas 24 horas)
      const recentSnapshot = data.snapshots.find((snapshot) => {
        const snapshotDate = new Date(snapshot.timestamp);
        const timeDiffMs = now.getTime() - snapshotDate.getTime();
        const timeDiffHours = timeDiffMs / (1000 * 60 * 60);
        return timeDiffHours < MIN_SNAPSHOT_INTERVAL_HOURS;
      });

      if (recentSnapshot) {
        // Ya existe un snapshot reciente, no guardar
        return;
      }

      // Crear nuevo snapshot
      const newSnapshot: HistoricalSnapshot = {
        timestamp: now.toISOString(),
        totalProjects: stats.totalProjects,
        activeProjects: stats.activeProjects,
        totalTenants: stats.totalTenants,
        activeTenants: stats.activeTenants,
        totalUsers: stats.totalUsers,
        activeUsersCount: stats.activeUsersCount,
      };

      // Agregar snapshot y ordenar por timestamp descendente
      const updatedSnapshots = [...data.snapshots, newSnapshot].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      setStoredData({ snapshots: updatedSnapshots });

      // Limpiar snapshots antiguos
      cleanOldSnapshots();
    },
    [getStoredData, setStoredData, cleanOldSnapshots]
  );

  /**
   * Calcula el indicador de cambio para una métrica específica
   *
   * @param metric - Nombre de la métrica a comparar
   * @param period - Período de comparación ('7d' o '30d')
   * @returns Indicador de cambio o null si no hay datos históricos suficientes
   *
   * @remarks
   * - Busca el snapshot más cercano al período especificado
   * - Retorna null si no hay datos históricos para comparar
   * - Maneja el caso especial cuando el valor anterior es 0
   *
   * @example
   * ```tsx
   * const change = getChange('totalProjects', '7d');
   * // Retorna: { value: 15.3, direction: 'up', period: '7d' }
   * ```
   */
  const getChange = useCallback(
    (metric: keyof SnapshotData, period: '7d' | '30d'): ChangeIndicator | null => {
      const data = getStoredData();
      if (data.snapshots.length < 2) {
        // No hay suficientes snapshots para comparar
        return null;
      }

      const daysAgo = period === '7d' ? 7 : 30;
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() - daysAgo);

      // Buscar el snapshot más cercano al período objetivo
      let closestSnapshot: HistoricalSnapshot | null = null;
      let smallestDiff = Infinity;

      for (const snapshot of data.snapshots) {
        const snapshotDate = new Date(snapshot.timestamp);
        const diff = Math.abs(targetDate.getTime() - snapshotDate.getTime());

        if (diff < smallestDiff) {
          smallestDiff = diff;
          closestSnapshot = snapshot;
        }
      }

      if (!closestSnapshot) {
        return null;
      }

      // Obtener el snapshot más reciente (actual)
      const currentSnapshot = data.snapshots[0];
      if (!currentSnapshot) {
        return null;
      }

      const currentValue = currentSnapshot[metric];
      const previousValue = closestSnapshot[metric];

      // Calcular cambio porcentual
      if (previousValue === 0) {
        // Caso especial: valor anterior era 0
        if (currentValue > 0) {
          return { value: 100, direction: 'up', period };
        }
        return { value: 0, direction: 'neutral', period };
      }

      const changePercent = ((currentValue - previousValue) / previousValue) * 100;
      const direction: 'up' | 'down' | 'neutral' =
        changePercent > 0 ? 'up' : changePercent < 0 ? 'down' : 'neutral';

      return {
        value: Math.abs(changePercent),
        direction,
        period,
      };
    },
    [getStoredData]
  );

  return useMemo(
    () => ({
      saveSnapshot,
      getChange,
      cleanOldSnapshots,
    }),
    [saveSnapshot, getChange, cleanOldSnapshots]
  );
}
